import { Dialog, DialogPanel } from "@headlessui/react";
import NiceModal, { useModal } from "@ebay/nice-modal-react";

function intervalToSeconds(intervalValue) {
  if (typeof intervalValue === "number") {
    return intervalValue;
  }
  if (!intervalValue || typeof intervalValue !== "string") {
    return 0;
  }

  const isoMatch = intervalValue.match(/^P(?:(\d+)D)?T(?:(\d+)H)?(?:(\d+)M)?/);
  if (isoMatch) {
    const days = Number(isoMatch[1] || 0);
    const hours = Number(isoMatch[2] || 0);
    const minutes = Number(isoMatch[3] || 0);
    return days * 24 * 60 * 60 + hours * 60 * 60 + minutes * 60;
  }

  const pgDayTimeMatch = intervalValue.match(
    /^(?:(\d+)\s+days?\s+)?(\d{1,2}):(\d{2}):(\d{2})$/,
  );
  if (pgDayTimeMatch) {
    const days = Number(pgDayTimeMatch[1] || 0);
    const hours = Number(pgDayTimeMatch[2] || 0);
    const minutes = Number(pgDayTimeMatch[3] || 0);
    const seconds = Number(pgDayTimeMatch[4] || 0);
    return days * 24 * 60 * 60 + hours * 60 * 60 + minutes * 60 + seconds;
  }

  return 0;
}

export default NiceModal.create(({ project, reminder, addReminderToState }) => {
  const modal = useModal();
  const initialSeconds = intervalToSeconds(reminder?.leadtime);
  const initialDays = Math.floor(initialSeconds / 86400);
  const initialHours = Math.floor((initialSeconds % 86400) / 3600);
  const initialMinutes = Math.floor((initialSeconds % 3600) / 60);

  async function handleSubmit(evt) {
    evt.preventDefault();
    const data = Object.fromEntries(new FormData(evt.target));
    const days = Number(data.days || 0);
    const hours = Number(data.hours || 0);
    const minutes = Number(data.minutes || 0);
    const totalSeconds = days * 24 * 60 * 60 + hours * 60 * 60 + minutes * 60;
    const leadtimeIso = `P${days}DT${hours}H${minutes}M`;
    const emailSubject = String(data.email_subject || "").trim();
    const emailBody = String(data.email_body || "").trim();

    if (totalSeconds <= 0) {
      alert("Bitte eine Vorlaufzeit größer als 0 eingeben.");
      return;
    }
    if (!emailSubject) {
      alert("Bitte einen E-Mail Betreff eingeben.");
      return;
    }
    if (!emailBody) {
      alert("Bitte einen E-Mail Text eingeben.");
      return;
    }

    const payload = {
      leadtime: leadtimeIso,
      email_subject: emailSubject,
      email_body: emailBody,
    };
    const method = reminder ? "PUT" : "POST";
    if (reminder) {
      payload.id = reminder.id;
    }

    const resp = await fetch(`/api/admin/reminder/${project.id}`, {
      method,
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!resp.ok) {
      alert("Es ist ein Fehler beim Speichern des Reminders aufgetreten.");
      return;
    }
    if (addReminderToState) {
      addReminderToState();
    }
    modal.hide();
  }

  return (
    <Dialog
      open={modal.visible}
      onClose={() => {
        modal.hide();
      }}
    >
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel>
            <div
              className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl sm:p-6"
              id="adduse-modal-panel"
            >
              <div>
                <form
                  className="mt-8 space-y-6"
                  method="POST"
                  onSubmit={handleSubmit}
                >
                  <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-3">
                    <div className="col-span-1">
                      <label htmlFor="days">Tage</label>
                      <input
                        id="days"
                        name="days"
                        type="number"
                        min={0}
                        defaultValue={initialDays}
                        className="relative block w-full appearance-none rounded--md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-stone-500 focus:outline-none focus:ring-stone-500 sm:text-sm"
                      />
                    </div>
                    <div className="col-span-1">
                      <label htmlFor="hours">Stunden</label>
                      <input
                        id="hours"
                        name="hours"
                        type="number"
                        min={0}
                        max={23}
                        defaultValue={initialHours}
                        className="relative block w-full appearance-none rounded--md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-stone-500 focus:outline-none focus:ring-stone-500 sm:text-sm"
                      />
                    </div>
                    <div className="col-span-1">
                      <label htmlFor="minutes">Minuten</label>
                      <input
                        id="minutes"
                        name="minutes"
                        type="number"
                        min={0}
                        max={59}
                        defaultValue={initialMinutes}
                        className="relative block w-full appearance-none rounded--md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-stone-500 focus:outline-none focus:ring-stone-500 sm:text-sm"
                      />
                    </div>
                  </div>
                  <div className="col-span-full">
                    <label htmlFor="email_subject">E-Mail Betreff</label>
                    <input
                      id="email_subject"
                      name="email_subject"
                      type="text"
                      required
                      defaultValue={reminder?.email_subject || ""}
                      className="relative block w-full appearance-none rounded--md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-stone-500 focus:outline-none focus:ring-stone-500 sm:text-sm"
                    />
                  </div>
                  <div className="col-span-full">
                    <label htmlFor="email_body">E-Mail Text</label>
                    <textarea
                      id="email_body"
                      name="email_body"
                      rows={6}
                      required
                      defaultValue={reminder?.email_body || ""}
                      className="relative block w-full appearance-none rounded--md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-stone-500 focus:outline-none focus:ring-stone-500 sm:text-sm"
                    />
                  </div>
                  <p className="mt-3 text-sm leading-6 text-gray-600">
                    Folgende Variablen können benutzt werden:
                    &#123;&#123;name&#125;&#125;,
                    &#123;&#123;booked_time&#125;&#125;,
                    &#123;&#123;booked_variant&#125;&#125;,
                    &#123;&#123;attendees_count&#125;&#125;,
                    &#123;&#123;cancel_url&#125;&#125; und
                    &#123;&#123;booked_time_unformatted&#125;&#125; (kann mit
                    <a href="https://docs.python.org/3/library/datetime.html#strftime-and-strptime-format-codes">
                      <code>strftime</code> formatiert werden
                    </a>
                    )
                  </p>

                  <div className="space-y-1">
                    <button
                      type="submit"
                      className="group relative flex w-full justify-center rounded-md border border-transparent bg-green-600 py-2 px-4 text-sm font-medium text-white hover:bg-stone-700 focus:outline-none focus:ring-2 focus:ring-stone-500 focus:ring-offset-2"
                    >
                      Speichern
                    </button>
                    <button
                      type="button"
                      onClick={() => modal.hide()}
                      className="group relative flex w-full justify-center rounded-md border border-transparent bg-red-800 py-2 px-4 text-sm font-medium text-white hover:bg-stone-700 focus:outline-none focus:ring-2 focus:ring-stone-500 focus:ring-offset-2"
                    >
                      Beenden
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
});
